import React, {
    Suspense,
    lazy,
    useEffect,
    useId,
    useRef,
    useState,
} from "react";
import { FiCheck, FiMessageSquare, FiSend, FiSmile, FiX } from "react-icons/fi";
import { v4 as uuidv4 } from "uuid";

import {
    BLOCKED_COMMENT_MESSAGE,
    ContainsBadWords,
} from "../../../Helpers/Comments";
import { describeVideoCommentError } from "../../../Hooks/Comments/Video/useVideoComments";

const VideoCommentEmojiPicker = lazy(() => import("./VideoCommentEmojiPicker"));

interface VideoCommentComposerProps {
    initialBody?: string;
    placeholder?: string;
    submitLabel?: string;
    autoFocus?: boolean;
    onSubmit: (body: string, idempotencyKey: string) => Promise<unknown>;
    onCancel?: () => void;
    onFocusChange?: (focused: boolean) => void;
}

interface TextSelection {
    start: number;
    end: number;
}

const VideoCommentComposer: React.FC<VideoCommentComposerProps> = ({
    initialBody = "",
    placeholder = "Join the conversation…",
    submitLabel = "Post comment",
    autoFocus = false,
    onSubmit,
    onCancel,
    onFocusChange,
}) => {
    const [body, setBody] = useState(initialBody);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [emojiOpen, setEmojiOpen] = useState(false);
    const pendingKeyRef = useRef<string | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const emojiControlRef = useRef<HTMLDivElement>(null);
    const emojiPanelRef = useRef<HTMLDivElement>(null);
    const emojiButtonRef = useRef<HTMLButtonElement>(null);
    const selectionRef = useRef<TextSelection>({
        start: initialBody.length,
        end: initialBody.length,
    });
    const emojiPanelId = useId();
    const emojiTitleId = useId();
    const countId = useId();
    const errorId = useId();
    const runeCount = Array.from(body).length;
    const containsBlockedWord = ContainsBadWords(body);
    const validationError = containsBlockedWord
        ? BLOCKED_COMMENT_MESSAGE
        : null;
    const displayedError = validationError ?? error;
    const valid =
        body.trim().length > 0 &&
        runeCount <= 2000 &&
        !containsBlockedWord;
    const compact = Boolean(onCancel);

    useEffect(() => {
        setBody(initialBody);
        setError(null);
        setEmojiOpen(false);
        pendingKeyRef.current = null;
        selectionRef.current = {
            start: initialBody.length,
            end: initialBody.length,
        };
    }, [initialBody]);

    useEffect(
        () => () => {
            onFocusChange?.(false);
        },
        [onFocusChange]
    );

    useEffect(() => {
        if (!emojiOpen) return;

        emojiPanelRef.current
            ?.querySelector<HTMLElement>(
                "button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex]:not([tabindex='-1'])"
            )
            ?.focus();

        const closeOnOutsidePointer = (event: PointerEvent) => {
            if (!emojiControlRef.current?.contains(event.target as Node)) {
                setEmojiOpen(false);
            }
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                setEmojiOpen(false);
                emojiButtonRef.current?.focus();
                return;
            }
            if (event.key !== "Tab") return;

            const focusable = Array.from(
                emojiPanelRef.current?.querySelectorAll<HTMLElement>(
                    "button:not(:disabled):not([tabindex='-1']), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex]:not([tabindex='-1'])"
                ) ?? []
            );
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener("pointerdown", closeOnOutsidePointer);
        document.addEventListener("keydown", closeOnEscape);
        return () => {
            document.removeEventListener("pointerdown", closeOnOutsidePointer);
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, [emojiOpen]);

    const rememberSelection = () => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        selectionRef.current = {
            start: textarea.selectionStart,
            end: textarea.selectionEnd,
        };
    };

    const updateBody = (nextBody: string, selection?: TextSelection) => {
        setBody(nextBody);
        setError(null);
        pendingKeyRef.current = null;
        if (selection) selectionRef.current = selection;
    };

    const insertEmoji = (emoji: string) => {
        const start = Math.min(selectionRef.current.start, body.length);
        const end = Math.min(Math.max(selectionRef.current.end, start), body.length);
        const nextBody = `${body.slice(0, start)}${emoji}${body.slice(end)}`;
        const nextCaret = start + emoji.length;

        updateBody(nextBody, { start: nextCaret, end: nextCaret });
        setEmojiOpen(false);

        const restoreCaret = () => {
            const textarea = textareaRef.current;
            textarea?.focus();
            textarea?.setSelectionRange(nextCaret, nextCaret);
            onFocusChange?.(true);
        };
        if (typeof window.requestAnimationFrame === "function") {
            window.requestAnimationFrame(restoreCaret);
        } else {
            window.setTimeout(restoreCaret, 0);
        }
    };

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!valid || submitting) return;

        const key = pendingKeyRef.current ?? `web-comment-${uuidv4()}`;
        pendingKeyRef.current = key;
        setSubmitting(true);
        setError(null);
        try {
            await onSubmit(body.trim(), key);
            pendingKeyRef.current = null;
            setBody("");
            setEmojiOpen(false);
            selectionRef.current = { start: 0, end: 0 };
            onFocusChange?.(false);
        } catch (submitError) {
            setError(describeVideoCommentError(submitError));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form
            className={`video-comments__composer ${compact ? "is-compact" : ""}`}
            onSubmit={submit}
            onFocusCapture={() => onFocusChange?.(true)}
            onBlurCapture={(event) => {
                const nextTarget = event.relatedTarget as Node | null;
                if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
                    onFocusChange?.(false);
                }
            }}
        >
            {!compact && (
                <div className="video-comments__composer-heading">
                    <span className="video-comments__composer-heading-icon" aria-hidden="true">
                        <FiMessageSquare />
                    </span>
                    <div>
                        <strong>Add to the discussion</strong>
                        <span>Share your perspective with the Ickonic community.</span>
                    </div>
                </div>
            )}

            <div className="video-comments__editor">
                <textarea
                    ref={textareaRef}
                    value={body}
                    autoFocus={autoFocus}
                    rows={initialBody ? 4 : 3}
                    placeholder={placeholder}
                    aria-label="Comment text"
                    aria-describedby={`${countId}${
                        displayedError ? ` ${errorId}` : ""
                    }`}
                    aria-invalid={runeCount > 2000 || containsBlockedWord}
                    onChange={(event) => {
                        const caret = event.currentTarget.selectionStart;
                        updateBody(event.target.value, { start: caret, end: caret });
                    }}
                    onClick={rememberSelection}
                    onKeyUp={rememberSelection}
                    onSelect={rememberSelection}
                />

                <div className="video-comments__composer-footer">
                    <div className="video-comments__composer-tools">
                        <div ref={emojiControlRef} className="video-comments__emoji-control">
                            <button
                                ref={emojiButtonRef}
                                type="button"
                                className="video-comments__emoji-trigger"
                                aria-controls={emojiPanelId}
                                aria-expanded={emojiOpen}
                                aria-haspopup="dialog"
                                disabled={submitting}
                                onClick={() => {
                                    rememberSelection();
                                    setEmojiOpen((open) => !open);
                                }}
                            >
                                <FiSmile aria-hidden="true" />
                                <span>Emoji</span>
                            </button>

                            {emojiOpen && (
                                <>
                                    <div
                                        className="video-comments__emoji-backdrop"
                                        aria-hidden="true"
                                        onPointerDown={() => {
                                            setEmojiOpen(false);
                                            emojiButtonRef.current?.focus();
                                        }}
                                    />
                                    <div
                                        ref={emojiPanelRef}
                                        id={emojiPanelId}
                                        className="video-comments__emoji-panel"
                                        role="dialog"
                                        aria-modal="true"
                                        aria-labelledby={emojiTitleId}
                                    >
                                        <div className="video-comments__emoji-panel-header">
                                            <strong id={emojiTitleId}>Choose an emoji</strong>
                                            <button
                                                type="button"
                                                aria-label="Close emoji picker"
                                                onClick={() => {
                                                    setEmojiOpen(false);
                                                    emojiButtonRef.current?.focus();
                                                }}
                                            >
                                                <FiX aria-hidden="true" />
                                            </button>
                                        </div>
                                        <Suspense
                                            fallback={
                                                <div
                                                    className="video-comments__emoji-loading"
                                                    role="status"
                                                >
                                                    Loading emoji…
                                                </div>
                                            }
                                        >
                                            <VideoCommentEmojiPicker onSelect={insertEmoji} />
                                        </Suspense>
                                    </div>
                                </>
                            )}
                        </div>
                        <span className="video-comments__emoji-hint">
                            Your device emoji keyboard works here too.
                        </span>
                    </div>

                    <div className="video-comments__composer-meta">
                        <span
                            id={countId}
                            className={runeCount > 2000 ? "is-error" : ""}
                            aria-live={runeCount >= 1800 ? "polite" : undefined}
                        >
                            {runeCount.toLocaleString()}/2,000
                        </span>
                        <div className="video-comments__composer-actions">
                            {onCancel && (
                                <button
                                    type="button"
                                    className="is-secondary"
                                    disabled={submitting}
                                    onClick={onCancel}
                                >
                                    Cancel
                                </button>
                            )}
                            <button
                                type="submit"
                                className="is-primary"
                                disabled={!valid || submitting}
                            >
                                {initialBody ? (
                                    <FiCheck aria-hidden="true" />
                                ) : (
                                    <FiSend aria-hidden="true" />
                                )}
                                <span>{submitting ? "Submitting…" : submitLabel}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {displayedError && (
                <p id={errorId} className="video-comments__form-error" role="alert">
                    {displayedError}
                </p>
            )}
        </form>
    );
};

export default VideoCommentComposer;
